import { initializeApp, getApps } from "firebase/app";
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
} from "firebase/messaging";
import { registerNotificationEndpoint, unregisterNotificationEndpoint } from "api/notification";
import firebaseConfig, { firebaseConfigured, vapidKey } from "./firebaseConfig";
import workerUrl from "./firebase-messaging-sw.js?worker&url";

const optInKey = (userId) => `kewordal:browser-notifications:enabled:${userId}`;
const tokenKey = (userId) => `kewordal:browser-notifications:token:${userId}`;

export const browserNotificationsEnabled = (userId) =>
  Boolean(userId && localStorage.getItem(optInKey(userId)) === "true");

export const browserNotificationAvailability = async () => {
  if (!firebaseConfigured) return { available: false, reason: "Firebase web configuration is missing from this build." };
  if (!vapidKey) return { available: false, reason: "The Firebase Web Push key is missing from this build." };
  if (window.isSecureContext === false) return { available: false, reason: "Browser notifications require HTTPS." };
  if (!("Notification" in window) || !("serviceWorker" in navigator)) {
    return { available: false, reason: "This browser does not support push notifications." };
  }
  if (Notification.permission === "denied") {
    return { available: false, reason: "Notifications are blocked for this site. Allow them in your browser's site settings." };
  }
  try {
    if (!(await isSupported())) {
      return { available: false, reason: "Firebase messaging is unavailable in this browser." };
    }
  } catch (error) {
    return { available: false, reason: `Firebase messaging could not start: ${error.message}` };
  }
  return { available: true, reason: "" };
};

const messagingForBrowser = () =>
  getMessaging(getApps().find((app) => app.name === "[DEFAULT]") || initializeApp(firebaseConfig));

export const syncBrowserNotifications = async (userId, requestPermission = false) => {
  if (!userId) {
    if (requestPermission) throw new Error("Your user ID is unavailable; please sign in again.");
    return false;
  }
  const availability = await browserNotificationAvailability();
  if (!availability.available) {
    if (requestPermission) throw new Error(availability.reason);
    return false;
  }
  if (requestPermission) {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      throw new Error(permission === "denied"
        ? "Notifications are blocked for this site. Allow them in your browser's site settings."
        : "Notification permission was not granted.");
    }
  }
  if (!(browserNotificationsEnabled(userId) || requestPermission) || Notification.permission !== "granted") return false;

  const registration = await navigator.serviceWorker.register(workerUrl);
  const token = await getToken(messagingForBrowser(), { vapidKey, serviceWorkerRegistration: registration });
  if (!token) {
    if (requestPermission) throw new Error("Firebase did not return a browser notification token.");
    return false;
  }
  const previousToken = localStorage.getItem(tokenKey(userId));
  await registerNotificationEndpoint(token);
  if (previousToken && previousToken !== token) {
    await unregisterNotificationEndpoint(previousToken).catch(() => {});
  }
  localStorage.setItem(tokenKey(userId), token);
  localStorage.setItem(optInKey(userId), "true");
  return true;
};

export const disableBrowserNotifications = async (userId, keepOptIn = false) => {
  if (!userId) return;
  const token = localStorage.getItem(tokenKey(userId));
  if (token) {
    try {
      await unregisterNotificationEndpoint(token);
    } catch (error) {
      // Deleting the FCM token still prevents delivery; NMS prunes invalid tokens.
      console.warn("Could not unregister browser token from NMS:", error);
    }
  }
  localStorage.removeItem(tokenKey(userId));
  if (!keepOptIn) localStorage.removeItem(optInKey(userId));
  if (firebaseConfigured && "serviceWorker" in navigator && await isSupported()) {
    await deleteToken(messagingForBrowser());
  }
};
